import { AUDIO_MIMETYPES, IMAGE_MIMETYPES, MimeType, VIDEO_MIMETYPES } from '@constants';
import { IMediaMetadata } from '@interface/media';
import { ITZIPFormat } from '@interface/tzip';
import { formatDuration } from '@utils/format';

export const getTZIP21GenericFormat = async (file: File, hash: string) => {
  return {
    uri: `ipfs://${hash}`,
    mimeType: file.type,
    fileSize: file.size,
    fileName: file.name,
  };
};

export const getTZIP21ImageFormat = async (
  file: File,
  hash: string,
  width: number,
  height: number
): Promise<ITZIPFormat> => {
  return {
    uri: `ipfs://${hash}`,
    mimeType: file.type,
    fileSize: file.size,
    fileName: file.name,
    dimensions: {
      value: `${width}x${height}`,
      unit: 'px',
    },
  };
};

export const getTZIP21VideoFormat = async (
  file: File,
  hash: string,
  width: number,
  height: number,
  duration: number
): Promise<ITZIPFormat> => {
  return {
    uri: `ipfs://${hash}`,
    mimeType: file.type,
    fileSize: file.size,
    fileName: file.name,
    dimensions: {
      value: `${width}x${height}`,
      unit: 'px',
    },
    duration: formatDuration(duration as number),
  };
};

export const getTZIP21AudioFormat = async (file: File, hash: string, duration: number): Promise<ITZIPFormat> => {
  return {
    uri: `ipfs://${hash}`,
    mimeType: file.type,
    fileSize: file.size,
    fileName: file.name,
    duration: formatDuration(duration as number),
  };
};

// get TZIP-21 format for any kind of file
export const getTZIPFormat = async (file: File, hash: string, meta: IMediaMetadata) => {
  const width = meta.width || 0;
  const height = meta.height || 0;
  const duration = meta.duration || 0;

  // sha-256 checksum
  // const arrayBuffer = await file.arrayBuffer()
  // const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
  // const hashArray = Array.from(new Uint8Array(hashBuffer));
  // const checksum = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

  if (IMAGE_MIMETYPES.includes(file.type)) {
    return getTZIP21ImageFormat(file, hash, width, height);
  }

  if (VIDEO_MIMETYPES.includes(file.type)) {
    return getTZIP21VideoFormat(file, hash, width, height, duration);
  }

  if (AUDIO_MIMETYPES.includes(file.type)) {
    return getTZIP21AudioFormat(file, hash, duration);
  }

  return getTZIP21GenericFormat(file, hash);
};

export const getTZIP21Dimensions = (width?: number, height?: number, aspect?: string) => {
  if (aspect) {
    return {
      value: aspect,
      unit: 'ratio',
    };
  }

  if (width && height) {
    return {
      value: `${width}x${height}`,
      unit: 'px',
    };
  }

  return {
    value: '100x100',
    unit: 'pct',
  };
};

export const getTZIP21WebFormat = async (
  hash: string,
  name: string,
  size: number,
  width?: number,
  height?: number,
  aspect?: string
): Promise<ITZIPFormat> => {
  const format = {
    uri: `ipfs://${hash}`,
    mimeType: MimeType.WEB,
    fileSize: size,
    fileName: name,
  };

  const dimensions = getTZIP21Dimensions(width, height, aspect);

  if (dimensions) {
    return { ...format, dimensions };
  }

  return format;
};
